import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./variables.css";
import "./SaveList.css"
import API_URL from "./config";

const SaveList = () => {
  const [saves, setSaves] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSaves();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Check if content fits on screen, and disable scrolling if it does
    const checkAndDisableScroll = () => {
      const wrapper = document.querySelector('.savelist-wrapper');
      if (!wrapper) return;

      const contentHeight = wrapper.scrollHeight;
      const viewportHeight = window.innerHeight;

      if (contentHeight <= viewportHeight) {
        // Content fits, disable scrolling
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
      } else {
        // Content overflows, allow scrolling
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      }
    };

    // Check on load and on window resize
    checkAndDisableScroll();
    window.addEventListener('resize', checkAndDisableScroll);

    return () => {
      // Cleanup: restore scrolling on unmount
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      window.removeEventListener('resize', checkAndDisableScroll);
    };
  }, [saves]);

  const fetchSaves = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user._id;
      
      if (!userId) {
        setError("Please log in to view saves");
        setLoading(false);
        return;
      }

      const res = await fetch(`${API_URL}/api/saves`, {
        headers: {
          "X-User-ID": userId,
        },
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || "Failed to load saves");
        return;
      }
      const data = await res.json();
      const sortedSaves = (data.saves || []).sort((a, b) => {
        const teamA = (a.teamName || a.teamId || "").toLowerCase();
        const teamB = (b.teamName || b.teamId || "").toLowerCase();
        return teamA.localeCompare(teamB);
      });
      setSaves(sortedSaves);
    } catch (err) {
      setError(err.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (saveId) => {
    // Two-step confirmation: first click sets pending, second click confirms
    if (pendingDeleteId !== saveId) {
      setPendingDeleteId(saveId);
      return;
    }

    // Clear pending and proceed with deletion
    setPendingDeleteId(null);

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user._id;

      const res = await fetch(`${API_URL}/api/saves/${saveId}`, {
        method: "DELETE",
        headers: {
          "X-User-ID": userId,
        },
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || "Failed to delete save");
        return;
      }
      // Refresh list
      fetchSaves();
      setError("");
    } catch (err) {
      setError(err.message || "Server error");
    }
  };

  const handleContinue = (saveId) => {
    // Store active save in localStorage
    localStorage.setItem("activeSaveId", saveId);
    // Navigate to dashboard
    navigate("/dashboard");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getSortedSaves = () => {
    return [...saves].sort((a, b) => {
      const valueA = (a.teamName || a.teamId || "").toLowerCase();
      const valueB = (b.teamName || b.teamId || "").toLowerCase();
      return valueA.localeCompare(valueB);
    });
  };

  return (
    <>
      <Navbar />
      <div className="savelist-wrapper">
        <div className="savelist-container">
          <div className="savelist-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
            <h2 style={{ margin: 0 }}>My Saves</h2>
            <Link to="/saves/new" style={{ textDecoration: "none", color: "var(--color-primary)" }}>
              + New Save
            </Link>
          </div>

          {error && <p className="error-message">{error}</p>}

          {loading ? (
            <p className="loading-message">Loading saves...</p>
          ) : saves.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📁</div>
              <p className="empty-state-text">No saves found.</p>
              <Link to="/saves/new" className="empty-state-action">
                Create your first save
              </Link>
            </div>
          ) : (
            <div className="savelist-grid">
              {getSortedSaves().map((save) => (
                <div key={save._id} className="save-card">
                  <div className="save-card-header">
                    <h3 className="save-card-title">{save.name}</h3>
                  </div>

                  <div className="save-card-content">
                    <div className="save-card-info">
                      <span className="save-card-info-label">Team:</span>
                      <span className="save-card-info-value">{save.teamName || save.teamId}</span>
                    </div>
                    <div className="save-card-info">
                      <span className="save-card-info-label">Season:</span>
                      <span className="save-card-info-value">{save.currentSeason || 2025}</span>
                    </div>
                    <span className="save-card-date">{formatDate(save.lastPlayed)}</span>
                  </div>

                  <div className="save-card-actions">
                    <button
                      onClick={() => handleContinue(save._id)}
                      className="save-btn save-btn-primary"
                    >
                      Continue
                    </button>
                    <button
                      onClick={() => handleDelete(save._id)}
                      className={`save-btn save-btn-danger ${pendingDeleteId === save._id ? "pending" : ""}`}
                    >
                      {pendingDeleteId === save._id ? "Confirm Delete" : "Delete"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SaveList;
